import {
  IonButtons,
  IonModal,
  IonTitle,
  IonToolbar,
  IonHeader,
  IonContent,
  IonIcon,
  IonText,
  IonSpinner,
} from '@ionic/react';
import HStack from 'components/HStack';
import DynamicButton from 'components/buttons/DynamicButton';
import AnimatedPodium from 'components/shared/AnimatedPodium';
import { trophy, medal, locationOutline, flagOutline } from 'ionicons/icons';
import {
  ComponentProps,
  FunctionComponent,
  useRef,
  useEffect,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ExploreElement } from '../../models/ExploreElementModel/ExploreElementModel';
import { useStores } from '../../models';
import { rankerService } from '../../services/freak_ranker';
import './RankingModal.css';
import { logger } from 'services/Logger';

export interface RankingModalProps extends ComponentProps<typeof IonModal> {
  isOpen: boolean;
  onDidDismiss: () => void;
  ranking: ExploreElement | null;
  onEditElement?: (element: ExploreElement) => void;
  onDeleteElement?: (element: ExploreElement) => void;
  onPersonClick?: (personName: string) => void;
}

interface RankingEntry {
  position: number;
  name: string;
  score: number;
  nationality?: string;
}

const RankingModal: FunctionComponent<RankingModalProps> = ({
  isOpen,
  onDidDismiss,
  ranking,
  onEditElement,
  onDeleteElement,
  onPersonClick,
  ...rest
}) => {
  const _t = useTranslation();
  const modalRef = useRef<HTMLIonModalElement>(null);
  const { currentUser } = useStores();

  // State für echte Ranking-Daten
  const [realRankingData, setRealRankingData] = useState<any[]>([]);
  const [isLoadingRankingData, setIsLoadingRankingData] = useState(false);
  const [_useGlobalRanking, setUseGlobalRanking] = useState(true);

  // Mock-Daten als Fallback
  const mockEntries: RankingEntry[] = [
    { position: 1, name: 'Marco Steinert', score: 98, nationality: 'Germany' },
    { position: 2, name: 'Lisa Schmidt', score: 95, nationality: 'Austria' },
    { position: 3, name: 'Jan Meyer', score: 92, nationality: 'Switzerland' },
    { position: 4, name: 'Anna Schulz', score: 88, nationality: 'Germany' },
    { position: 5, name: 'Tim Müller', score: 85, nationality: 'Netherlands' },
  ];

  // Lade echte Ranking-Daten wenn Modal geöffnet wird
  useEffect(() => {
    const loadRankingData = async () => {
      if (!isOpen || !ranking) return;

      // Prüfe ob es sich um ein Global Ranking handelt
      if (ranking.id.startsWith('ranking_global_')) {
        setUseGlobalRanking(true);
        return;
      }

      // Für benutzerdefinierte Rankings: Lade Daten basierend auf Ranking-Eigenschaften
      setIsLoadingRankingData(true);
      setUseGlobalRanking(false);

      try {
        const filters = {
          locations:
            ranking.location && ranking.location !== 'Global'
              ? [ranking.location]
              : [],
          nationalities:
            ranking.nationality && ranking.nationality !== 'Global'
              ? [ranking.nationality]
              : [],
          genders:
            ranking.gender && ranking.gender !== 'Mixed'
              ? [ranking.gender]
              : [],
          ageRange: undefined as { min: number; max: number } | undefined,
          searchQuery: '',
        };

        // Wenn ein spezifisches Alter gesetzt ist, erstelle einen Altersbereich
        if (ranking.age && ranking.age > 0) {
          const ageMargin = 5; // ±5 Jahre
          filters.ageRange = {
            min: Math.max(4, ranking.age - ageMargin),
            max: Math.min(80, ranking.age + ageMargin),
          };
        }

        // Entscheide zwischen globalem und Custom Ranking
        let rankingData = [];
        if (
          filters.locations.length > 0 ||
          filters.nationalities.length > 0 ||
          filters.genders.length > 0 ||
          filters.ageRange
        ) {
          // Custom Ranking mit Filtern
          rankingData = await rankerService.getCustomRanking(filters, 0, 20);
        } else {
          // Global Ranking als Fallback
          rankingData = await rankerService.getGlobalRanking(0, 20);
        }
        setRealRankingData(rankingData);
      } catch (error) {
        logger.error('❌ Failed to load ranking data:', error);
        setRealRankingData([]);
      } finally {
        setIsLoadingRankingData(false);
      }
    };

    loadRankingData();
  }, [isOpen, ranking]);

  const handleClose = () => {
    onDidDismiss?.();
    modalRef.current?.dismiss();
  };

  // Prüfe, ob der aktuelle Benutzer der Besitzer des Rankings ist
  const isOwner = ranking?.isOwnedByUser(currentUser?.uid);
  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1:
        return <IonIcon icon={trophy} className="position-icon gold" />;
      case 2:
        return <IonIcon icon={medal} className="position-icon silver" />;
      case 3:
        return <IonIcon icon={medal} className="position-icon bronze" />;
      default:
        return <span className="position-number">{position}</span>;
    }
  };

  // Extrahiere die Top 3 Namen aus den verfügbaren Ranking-Daten
  const getTopThreeWinners = () => {
    let dataSource: any[] = [];

    // Bestimme die Datenquelle in der gleichen Priorität wie im Leaderboard
    if (ranking?.rankings && ranking.rankings.length > 0) {
      dataSource = ranking.rankings;
    } else if (realRankingData.length > 0) {
      dataSource = realRankingData.map((user, index) => ({
        position: index + 1,
        userName: user.displayName || user.name || 'Unknown',
        score: user.score || 0,
      }));
    } else {
      dataSource = mockEntries.map((entry) => ({
        position: entry.position,
        userName: entry.name,
        score: entry.score,
      }));
    }

    return {
      first:
        dataSource.find((entry) => entry.position === 1)?.userName || 'Platz 1',
      second:
        dataSource.find((entry) => entry.position === 2)?.userName || 'Platz 2',
      third:
        dataSource.find((entry) => entry.position === 3)?.userName || 'Platz 3',
    };
  };

  // Extrahiere die Top 3 Scores aus den verfügbaren Ranking-Daten
  const getTopThreeScores = () => {
    let dataSource: any[] = [];

    // Bestimme die Datenquelle in der gleichen Priorität wie im Leaderboard
    if (ranking?.rankings && ranking.rankings.length > 0) {
      dataSource = ranking.rankings;
    } else if (realRankingData.length > 0) {
      dataSource = realRankingData.map((user, index) => ({
        position: index + 1,
        userName: user.displayName || user.name || 'Unknown',
        score: user.score || 0,
      }));
    } else {
      dataSource = mockEntries.map((entry) => ({
        position: entry.position,
        userName: entry.name,
        score: entry.score,
      }));
    }

    return {
      first: dataSource.find((entry) => entry.position === 1)?.score || 0,
      second: dataSource.find((entry) => entry.position === 2)?.score || 0,
      third: dataSource.find((entry) => entry.position === 3)?.score || 0,
    };
  };

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
      className="edit-modal"
      {...rest}
    >
      <IonHeader className="ion-no-border">
        {/* <div className="modal-image-svg">
          <img src={'/assets/images/Pic_Continent_Filter.svg'} alt="Header" />
        </div> */}
        <IonToolbar className="edit-modal-toptoolbar">
          <IonButtons slot="start">
            {/* Edit und Delete Buttons nur für den Besitzer anzeigen */}
            {isOwner && ranking && (
              <>
                <DynamicButton
                  text={<Trans i18nKey="EDIT" />}
                  iconName="AddUpIcon"
                  buttonType="icon-full"
                  size="xlarge"
                  fill="clear"
                  className="spnx-button"
                  onClick={() => onEditElement?.(ranking)}
                />
                <DynamicButton
                  text={<Trans i18nKey="DELETE" />}
                  iconName="CancelIcon"
                  buttonType="icon-full"
                  size="xlarge"
                  fill="clear"
                  className="spnx-button"
                  onClick={() => onDeleteElement?.(ranking)}
                />
              </>
            )}
          </IonButtons>
          <IonTitle>
            <Trans i18nKey="RANKING_DETAILS" />
          </IonTitle>
          <IonButtons slot="end">
            <DynamicButton
              text={<Trans i18nKey="CANCEL" />}
              iconName="CancelUpIcon"
              buttonType="icon-full"
              size="xlarge"
              fill="clear"
              className="spnx-button"
              onClick={handleClose}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent
        style={{ '--background': 'transparent', '--border-colour': 0 }}
      >
        {ranking ? (
          <div className="ranking-modal-wrapper">
            <div className="ranking-modal-header">
              <h1>{ranking.name}</h1>
            </div>

            <div className="ranking-modal-details">
              <HStack className="ranking-detail-row">
                {ranking.country && (
                    <div className="ranking-detail-item">
                    <IonIcon icon={flagOutline} style={{ color: 'white' }} />
                    <IonText>{ranking.country}</IonText>
                    </div>
                )}
                {ranking.nationality && (
                  <div className="ranking-detail-item">
                    <IonIcon icon={locationOutline} style={{ color: 'white' }} />
                    <IonText>{ranking.nationality}</IonText>
                  </div>
                )}
              </HStack>
            </div>

            {/* Animated Podium */}
            <div className="ranking-modal-podium">
              <AnimatedPodium
                winners={getTopThreeWinners()}
                scores={getTopThreeScores()}
                onPersonClick={onPersonClick}
              />
            </div>

            {/* Description nur anzeigen wenn vorhanden */}
            {/* {ranking.description && (
              <div className="ranking-modal-description">
                <p>{ranking.description}</p>
              </div>
            )} */}

            <div className="ranking-leaderboard">
              <div className="ranking-entries">
                {isLoadingRankingData ? (
                  <IonSpinner
                    name="crescent"
                    style={{ width: 48, height: 48 }}
                  />
                ) : (
                  <>
                    {/* Zeige echte Ranking-Daten falls vorhanden - ab Platz 4 */}
                    {ranking.rankings && ranking.rankings.length > 0
                      ? ranking.rankings
                          .filter((entry) => entry.position > 3)
                          .slice(0, 7)
                          .map((entry) => (
                            <div
                              key={entry.position}
                              className="ranking-modal-item"
                              onClick={() =>
                                onPersonClick && onPersonClick(entry.userName)
                              }
                              style={{
                                cursor: onPersonClick ? 'pointer' : 'default',
                              }}
                            >
                              <div className="ranking-position">
                                {getPositionIcon(entry.position)}
                              </div>
                              <div className="ranking-info">
                                <div className="participant-name">
                                  {entry.userName}
                                </div>
                              </div>
                              <div className="participant-score">
                                {entry.score}
                              </div>
                              {entry.nationality && (
                                <IonIcon
                                  icon={flagOutline}
                                  className="nationality-flag"
                                  title={entry.nationality}
                                />
                              )}
                            </div>
                          ))
                      : realRankingData.length > 0
                        ? 
                          realRankingData.slice(3, 10).map((user, index) => (
                            <div
                              key={index + 4}
                              className="ranking-modal-item"
                              onClick={() =>
                                onPersonClick &&
                                onPersonClick(user.displayName || user.name)
                              }
                              style={{
                                cursor: onPersonClick ? 'pointer' : 'default',
                              }}
                            >
                              <div className="ranking-position">
                                {getPositionIcon(index + 4)}
                              </div>
                              <div className="ranking-info">
                                <div className="participant-name">
                                  {user.displayName || user.name || 'Unknown'}
                                </div>
                              </div>
                              <div className="participant-score">
                                {user.score || 0}
                              </div>
                              {user.nationality && (
                                <IonIcon
                                  icon={flagOutline}
                                  className="nationality-flag"
                                  title={user.nationality}
                                />
                              )}
                            </div>
                          ))
                        : 
                          mockEntries
                            .filter((entry) => entry.position > 3)
                            .map((entry) => (
                              <div
                                key={entry.position}
                                className="ranking-modal-item"
                                onClick={() =>
                                  onPersonClick && onPersonClick(entry.name)
                                }
                                style={{
                                  cursor: onPersonClick ? 'pointer' : 'default',
                                }}
                              >
                                <div className="ranking-position">
                                  {getPositionIcon(entry.position)}
                                </div>
                                <div className="ranking-info">
                                  <div className="participant-name">
                                    {entry.name}
                                  </div>
                                </div>
                                <div className="participant-score">
                                  {entry.score}
                                </div>
                                {entry.nationality && (
                                  <IonIcon
                                    icon={flagOutline}
                                    className="nationality-flag"
                                    title={entry.nationality}
                                  />
                                )}
                              </div>
                            ))}
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="ranking-modal-empty">
            <p>
              <Trans i18nKey="NO_RANKING_SELECTED" />
            </p>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default RankingModal;
